'use client'

import React, { useMemo } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { RiMoreLine } from '@remixicon/react'
import { hamburgerMenuStore } from '../../../shared/Header/Store/hamburgerMenuStore'
import { DashboardNavGroup, flattenNavItems, isNavItemActive } from './navigation'

export interface MobileTabBarProps {
  navGroups: DashboardNavGroup[]
  /** Adds a trailing tab that opens the nav drawer. Off when every item fits. */
  showMore?: boolean
  moreLabel?: string
}

/**
 * The persistent bottom navigation.
 *
 * Tabs come from the items marked `mobilePrimary` in the nav config — not from a
 * second list declared here, which is how the previous tab bar drifted out of
 * step with the sidebar it was meant to mirror.
 *
 * A dashboard with more destinations than fit gets a "More" tab, which opens the
 * same drawer as the header's menu button: one overflow surface, two ways in,
 * and nothing further than two taps away.
 */
export const MobileTabBar: React.FC<MobileTabBarProps> = ({
  navGroups,
  showMore = true,
  moreLabel = 'More',
}) => {
  const pathname = usePathname()
  const { open } = hamburgerMenuStore()

  const tabs = useMemo(
    () => flattenNavItems(navGroups).filter((item) => item.mobilePrimary),
    [navGroups],
  )

  // "More" carries the highlight whenever the current screen is not one of the
  // tabs, so the bar never looks as though nothing is selected.
  const anyTabActive = tabs.some((item) => isNavItemActive(pathname, item))

  return (
    <div className="w-full bg-white border-t border-slate-200">
      <nav className="w-full px-3 py-2 bg-white outline outline-1 outline-zinc-200 flex justify-center items-center overflow-hidden">
        <div className="flex-1 flex justify-start items-start">
          {tabs.map((item) => {
            const active = isNavItemActive(pathname, item)
            return (
              <div key={item.id} className="flex-1">
                <Link
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  className="dashboard-nav-link w-full min-h-11 inline-flex flex-col justify-start items-center gap-1 cursor-pointer"
                >
                  {/* Same rule as the sidebar: neutral until it is the current
                      tab, so the accent means "you are here". */}
                  <span
                    className={`p-1.5 rounded-lg inline-flex justify-center items-center ${
                      active
                        ? 'bg-dashboard-accent-soft text-dashboard-accent'
                        : 'text-dashboard-faint'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span
                    className={`text-center text-dashboard-text text-[10px] font-poppins leading-4 truncate max-w-full px-0.5 ${
                      active ? 'font-semibold' : 'font-medium'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              </div>
            )
          })}

          {showMore && (
            <div className="flex-1">
              <button
                type="button"
                onClick={open}
                aria-haspopup="dialog"
                className="dashboard-nav-link w-full min-h-11 inline-flex flex-col justify-start items-center gap-1 cursor-pointer"
              >
                <span
                  className={`p-1.5 rounded-lg inline-flex justify-center items-center text-dashboard-accent ${
                    anyTabActive ? '' : 'bg-dashboard-accent-soft'
                  }`}
                >
                  <RiMoreLine size={20} />
                </span>
                <span
                  className={`text-center text-dashboard-text text-[10px] font-poppins leading-4 ${
                    anyTabActive ? 'font-medium' : 'font-semibold'
                  }`}
                >
                  {moreLabel}
                </span>
              </button>
            </div>
          )}
        </div>
      </nav>
    </div>
  )
}
